import type { ApiLogger } from '@klareospace/pharos-lib-types';

import { TELEMETRY_METRIC_NAMES, TELEMETRY_NETWORK_ERROR_STATUS } from '../constants';
import type { TelemetryClient } from './types';

/** Strip the query string so telemetry dimensions never carry request parameters. */
function sanitizeUrl(url: string): string {
  return url.split('?')[0];
}

/**
 * Create an API logger that forwards request outcomes to the telemetry client.
 *
 * `onError` captures method, url, HTTP status, and duration as custom dimensions on each
 * tracked exception. `onResponse` tracks a `http-request-duration-ms` custom metric for
 * every successful request. Use with `composeApiLoggers` to add telemetry alongside the
 * existing console logger without replacing it.
 *
 * The logger is fail-silent: errors thrown by the telemetry client are swallowed
 * by the client itself — the API logger does not add extra try/catch.
 *
 * @param client - The telemetry client to forward request outcomes to.
 * @returns An {@link ApiLogger} implementation.
 */
export function createTelemetryApiLogger(client: TelemetryClient): ApiLogger {
  return {
    onError: (method: string, url: string, status: number | undefined, durationMs: number, error: unknown): void => {
      client.trackException(error, {
        durationMs: String(Math.round(durationMs)),
        method: method.toUpperCase(),
        status: String(status ?? TELEMETRY_NETWORK_ERROR_STATUS),
        url: sanitizeUrl(url),
      });
    },
    onResponse: (method: string, url: string, status: number, durationMs: number): void => {
      client.trackMetric(TELEMETRY_METRIC_NAMES.HTTP_REQUEST_DURATION_MS, durationMs, {
        method: method.toUpperCase(),
        status: String(status),
        url: sanitizeUrl(url),
      });
    },
  };
}
